import { useEffect, useMemo, useState } from 'react'
import { collection, query, where } from 'firebase/firestore'
import type { DocumentData, QuerySnapshot } from 'firebase/firestore'
import { db, isOtaBoot } from '../lib/firebase'
import { getDocs, getDocsCacheFirst } from '../lib/firestore'
import { useArtistsByIds } from './useArtistsByIds'
import { useResetOnChange } from './useResetOnChange'
import { chunk, FIRESTORE_IN_LIMIT } from '../lib/chunk'
import { log } from '../lib/logger'
import { eventContentVersion } from '../lib/eventVersion'
import type { Event } from '../types'
import { entryArtistIds } from '@shared/lineupActs'

interface LineupRow {
  eventId: string
  /** The act's performers (one for a solo row). */
  artistIds: string[]
  tier?: number
}

const EMPTY_MAP = new Map<string, string[]>()

// `${eventId}@${version}` → resolved lineup names. Survives unmount so moving
// between Home and Discover renders lineup subtitles on the first frame
// instead of waiting on the IndexedDB reads below.
const lineupMemCache = new Map<string, string[]>()

function readMemCache(key: string): Map<string, string[]> | null {
  if (!key) return null
  const result = new Map<string, string[]>()
  for (const entry of key.split(',')) {
    const names = lineupMemCache.get(entry)
    if (!names) return null
    if (names.length > 0) result.set(entry.split('@')[0], names)
  }
  return result
}

// eventId → the content version whose lineup the local cache last synced from the
// network. One JSON map (not a key per event) so it stays bounded.
const VERSIONS_KEY = 'beacon_lineup_versions'
const MAX_VERSIONS = 500

function readVersions(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(VERSIONS_KEY) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}

function writeVersions(synced: Map<string, string>) {
  if (isOtaBoot) return
  try {
    const all = { ...readVersions() }
    for (const [id, v] of synced) {
      delete all[id]
      all[id] = v
    }
    const entries = Object.entries(all)
    localStorage.setItem(VERSIONS_KEY, JSON.stringify(Object.fromEntries(entries.slice(-MAX_VERSIONS))))
  } catch { /* quota */ }
}

function fetchLineups(ids: string[], read: typeof getDocs) {
  return Promise.all(
    chunk(ids, FIRESTORE_IN_LIMIT).map(batch =>
      read(query(collection(db, 'lineup'), where('eventId', 'in', batch))),
    ),
  )
}

function toRows(snaps: QuerySnapshot<DocumentData>[]): LineupRow[] {
  return snaps.flatMap(snap =>
    snap.docs.map(d => {
      const data = d.data()
      return { eventId: data.eventId, artistIds: entryArtistIds(data), tier: data.tier }
    }),
  )
}

// Lineup artist names per event, cache-first. An event whose content version
// (moved by every lineup write — see eventContentVersion) differs from the one its cached lineup was
// synced at is refetched from the network in the background, so lineup
// additions arrive and events missing from the cache fill in.
export function useEventLineups(events: Pick<Event, 'id' | 'contentUpdatedAt'>[]): {
  lineups: Map<string, string[]>
  loading: boolean
} {
  const [rows, setRows] = useState<LineupRow[]>([])

  const key = useMemo(
    () => [...new Set(events.map(e => `${e.id}@${eventContentVersion(e) ?? 0}`))].sort().join(','),
    [events],
  )
  const hasIds = key.length > 0
  // Starts true for a non-empty id set so the first render reports loading
  // rather than a momentary "no lineups" before the fetch below starts.
  const [fetching, setFetching] = useResetOnChange(key, () => hasIds)

  useEffect(() => {
    if (!hasIds) return
    let cancelled = false

    const versions = new Map(key.split(',').map(entry => entry.split('@') as [string, string]))
    const ids = [...versions.keys()]
    const synced = readVersions()
    const stale = ids.filter(id => synced[id] !== versions.get(id))

    fetchLineups(ids, getDocsCacheFirst)
      .then(snaps => {
        if (cancelled) return
        const cached = toRows(snaps)
        setRows(cached)
        // Nothing cached at all but some events unsynced: keep loading until
        // the network lands instead of flashing lineup-less cards.
        if (cached.length > 0 || stale.length === 0) setFetching(false)
        if (stale.length === 0) return

        const staleSet = new Set(stale)
        fetchLineups(stale, getDocs)
          .then(netSnaps => {
            if (cancelled) return
            const fresh = toRows(netSnaps)
            setRows(prev => [...prev.filter(r => !staleSet.has(r.eventId)), ...fresh])
            setFetching(false)
            writeVersions(new Map(stale.map(id => [id, versions.get(id)!])))
          })
          .catch(err => {
            log('lineup', 'network refetch failed', { events: stale.length, error: String(err) })
            if (!cancelled) setFetching(false)
          })
      })
      .catch(err => {
        log('lineup', 'cache read failed', { error: String(err) })
        if (!cancelled) setFetching(false)
      })

    return () => { cancelled = true }
  }, [key, hasIds, setFetching])

  const artistIds = useMemo(
    () => [...new Set(rows.flatMap(r => r.artistIds))],
    [rows],
  )
  const { artists, loading: artistsLoading } = useArtistsByIds(artistIds)

  const resolved = hasIds && !fetching && !artistsLoading

  const computed = useMemo(() => {
    if (!resolved) return EMPTY_MAP
    const grouped = new Map<string, LineupRow[]>()
    for (const r of rows) {
      let list = grouped.get(r.eventId)
      if (!list) { list = []; grouped.set(r.eventId, list) }
      list.push(r)
    }
    const result = new Map<string, string[]>()
    for (const [eventId, entries] of grouped) {
      const sorted = entries.sort((a, b) => (a.tier ?? 99) - (b.tier ?? 99))
      const names = [...new Set(sorted
        .flatMap(e => e.artistIds.map(id => artists[id]?.name))
        .filter((n): n is string => !!n))]
      if (names.length > 0) result.set(eventId, names)
    }
    return result
  }, [resolved, rows, artists])

  useEffect(() => {
    if (!resolved) return
    for (const entry of key.split(',')) {
      lineupMemCache.set(entry, computed.get(entry.split('@')[0]) ?? [])
    }
  }, [resolved, key, computed])

  // Read on every unresolved render, not memoized on `key`: a background refresh
  // that drops back to unresolved (new rows naming an artist not cached yet)
  // must find the lineup the effect above just cached rather than blank the subtitle.
  const memHit = resolved ? null : readMemCache(key)
  if (memHit) return { lineups: memHit, loading: false }
  return { lineups: computed, loading: fetching || artistsLoading }
}
